import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { combineLatest, Observable, of, Subscription, zip } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { SearchType } from '@search-bar/entities/search-type';
import { AppFacade } from '@app/app.facade';
import { HomeFacade } from '@app/modules/home/home.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { Place } from '@app/entities/place';
import { Airport } from '@app/modules/core/interfaces/airport';
import { HomeDataResponse } from '@shared/home-sections/entities/home-data.response';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { FlightType } from '@search-bar/entities/flight-type';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import {
  compareToCurrentDateWithoutTime,
  formatDateByRegex
} from '@ux/mappers/date.mapper';
import { PassengerArrangement, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { DeeplinkPlacePipe } from '@app/pipes/deeplink-place.pipe';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { mapPayload } from '@flight-results/mappers/search-payload.mapper';
import { FlightClass, SearchFlightClassByTypes } from '@shared/base-search-bar/entities/flight-class';
import { isEmpty } from '@app/util/util';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-flights',
  templateUrl: './flights.container.html',
  styleUrls: ['./flights.container.sass'],
  standalone: false
})
export class FlightsContainer implements OnInit, OnDestroy {

  public searchType: SearchType = SearchType.HOME_FLIGHT;
  public source: EVENT_SOURCE = EVENT_SOURCE.TRAVEL_HOME;

  private calendarRoute: string = 'calendar';
  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly facade: AppFacade,
    private readonly router: Router,
    private readonly baseSearchBarFacade: BaseSearchBarFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly placePipe: DeeplinkPlacePipe,
    protected route: ActivatedRoute,
    protected homeFacade: HomeFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean) { }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.baseSearchBarFacade.resetBranchCode();
      this.baseSearchBarFacade.resetSalesChannel();
      this.baseSearchBarFacade.resetPackageStep();
      this.baseSearchBarFacade.includeBaggage(false);
      this.subscriptions.add(
        this.isMobile$.subscribe(isMobile => {
          if (!isMobile) this.router.navigate(['/']);
        })
      );
      this.listenSearchParams();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get departureDate$(): Observable<Date> {
    return this.baseSearchBarFacade.departureDate$;
  }

  get arrivalDate$(): Observable<Date> {
    return this.baseSearchBarFacade.arrivalDate$;
  }

  get flightType$(): Observable<FlightType> {
    return this.baseSearchBarFacade.flightType$;
  }

  get places$(): Observable<Place[]> {
    return this.facade.places$;
  }

  get airports$(): Observable<Airport[]> {
    return this.facade.airports$;
  }

  get homeData$(): Observable<HomeDataResponse> {
    return this.homeFacade.homeData$;
  }

  get loadDefaultplaces(): boolean {
    return !!this.route?.snapshot?.data?.['loadDefaultplaces'];
  }

  private listenSearchParams(): void {
    this.subscriptions.add(
      combineLatest([
        this.route.paramMap,
        this.route.queryParamMap?.pipe(take(1))
      ]).pipe(
        switchMap(([params, queries]) => zip(
          of({ params, queries }),
          params.get('arrival') ?
            this.placePipe.transform(params.get('arrival')).pipe(filter((place) => !isEmpty(place))) : of(null),
          params.get('departure') ?
            this.placePipe.transform(params.get('departure')).pipe(filter((place) => !isEmpty(place))) : of(null),
          this.isMobile$.pipe(take(1)),
        )),
        map(([{ params, queries }, arrival, departure, isMobile]) => ({ params, queries, arrival, departure, isMobile })),
        tap(({ params, queries, arrival, departure, isMobile }) => {
          if (this.validatePopulated(params)) {
            const branchCodeQueries = queries.get('branch_code');
            const salesChannelQueries = queries.get('sales_channel');
            const departurePlaceParam = {
              code: departure.cityCode, country: departure.country,
              description: departure.name, is_airport: departure.is_airport
            } as SelectAutocompletePlace;
            const arrivalPlaceParam = {
              code: arrival.cityCode, country: arrival.country,
              description: arrival.name, is_airport: arrival.is_airport
            } as SelectAutocompletePlace;
            const paramsMapp = mapPayload(params['params'], isMobile, false);
            this.setPassengerArrangement({
              id: PassengerType.ADULTS,
              quantity: paramsMapp.Adults ? parseInt(paramsMapp.Adults) : 1
            });
            this.setPassengerArrangement({
              id: PassengerType.CHILDREN,
              quantity: paramsMapp.Children ? parseInt(paramsMapp.Children) : 0
            });
            this.setPassengerArrangement({
              id: PassengerType.INFANTS,
              quantity: paramsMapp.Infants ? parseInt(paramsMapp.Infants) : 0
            });
            this.selectCalendarDates(params);
            this.baseSearchBarFacade.selectFlightClass(
              SearchFlightClassByTypes[params.get('flight_class').toUpperCase()] as FlightClass);
            this.baseSearchBarFacade.selectFlightType(
              params.get('flight_type').toUpperCase() as FlightType);
            this.baseSearchBarFacade.selectPlace(
              { ...arrivalPlaceParam, type: PlaceType.ARRIVAL }, false, this.searchType);
            this.baseSearchBarFacade.selectPlace(
              { ...departurePlaceParam, type: PlaceType.DEPARTURE }, false, this.searchType);
            if (branchCodeQueries) {
              this.baseSearchBarFacade.setBranchCode(branchCodeQueries);
            }
            if (salesChannelQueries) {
              this.baseSearchBarFacade.setSalesChannel(salesChannelQueries);
            }
          }
        }),
        switchMap(() => this.baseSearchBarFacade.searchData$.pipe(
          filter((data) => !isEmpty(data?.departurePlace?.code) && !isEmpty(data?.arrivalPlace?.code)),
          take(1),
          tap((searchData) => {
            if (!this.loadDefaultplaces) {
              this.baseSearchBarFacade.saveSearchData(searchData, SearchBy.FLIGHTS);
            }
          })
        ))
      ).subscribe()
    );
  }

  public selectCalendarDates(params: ParamMap): void {
    let departureDateFormat = formatDateByRegex(params.get('departure_date'));
    let arrivalDateFormat = params.get('arrival_date') ? formatDateByRegex(params.get('arrival_date')) : null;
    if (compareToCurrentDateWithoutTime(departureDateFormat)) {
      departureDateFormat = null;
    }
    if (compareToCurrentDateWithoutTime(arrivalDateFormat)) {
      arrivalDateFormat = null;
    }
    this.baseSearchBarFacade.selectCalendarDates([departureDateFormat, arrivalDateFormat]);
  }

  public setPassengerArrangement(event: PassengerArrangement): void {
    this.baseSearchBarFacade.setPassengerArrangement(event);
  }

  public showMobileCalendar(type: CalendarField): void {
    this.router.navigate(
      [{ outlets: { search: [this.calendarRoute] } }],
      {
        state: {
          activeType: type,
          departurePlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.FLIGHT_DEPARTURE',
          returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.FLIGHT_RETURN',
          searchType: SearchType.HOME_FLIGHT
        },
        relativeTo: this.route, skipLocationChange: true
      });
  }

  private validatePopulated(params: ParamMap): boolean {
    return (!!params.get('flight_type') &&
      !!params.get('flight_class') && !!params.get('departure') &&
      !!params.get('arrival') && !!params.get('adults') &&
      !!params.get('children') && !!params.get('infants') &&
      !!params.get('baggage_included'));
  }
}
